import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, XIcon } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme";
import Pagination from "../../../components/shared_components/Pagination";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

// "2 days ago" style relative time for the card meta line.
function relTime(iso) {
    if (!iso) return "never";
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return "never";
    const day = 86400000;
    const days = Math.floor(diff / day);
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return months === 1 ? "1 month ago" : `${months} months ago`;
    const years = Math.floor(days / 365);
    return years === 1 ? "1 year ago" : `${years} years ago`;
}

// Job Roles — searches are grouped by the GenieHire job they're tied to. Clicking a
// job role opens its detail (searches + shortlisted / contacted / saved).
const Projects = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const orgId = localStorage.getItem("current_org_id");

    const [jobRoles, setJobRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);

    // Visibility is org-wide — everyone in the org sees the same job roles.
    const fetchJobRoles = useCallback(() => {
        if (!orgId) { setLoading(false); return; }
        setLoading(true);
        axios
            .get(`${SOURCING_URL}/sourcing/job-roles`, { params: { org_id: orgId } })
            .then(({ data }) => setJobRoles(data.job_roles || []))
            .catch(() => setError("Failed to load job roles."))
            .finally(() => setLoading(false));
    }, [orgId]);

    useEffect(() => { fetchJobRoles(); }, [fetchJobRoles]);

    const filteredJobRoles = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const list = !q
            ? [...jobRoles]
            : jobRoles.filter(j => (j.job_role || "").toLowerCase().includes(q));
        // Most recent activity first.
        return list.sort((a, b) => new Date(b.last_activity || 0) - new Date(a.last_activity || 0));
    }, [jobRoles, searchQuery]);

    // Keep the current page valid as filters/results change.
    useEffect(() => { setCurrentPage(1); }, [searchQuery]);

    const totalItems = filteredJobRoles.length;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentJobRoles = filteredJobRoles.slice(indexOfFirstItem, indexOfLastItem);

    // Show the "New search" tile like the reference "Build from scratch" card —
    // only on the first, unfiltered page so it isn't repeated across pages.
    const showAddCard = currentPage === 1 && !searchQuery.trim();

    return (
        <div className="h-full flex flex-col border rounded-2xl border-gray-700/50 dark:glass-effect bg-bgSecondary">
            <div className="flex flex-col flex-1 w-full">
                <div className="flex-1 w-full px-6 py-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 py-4 mb-4">
                        <div className="flex items-center gap-4">
                            <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary font-normal text-left pl-10">
                                Job Roles
                            </h1>
                        </div>
                        {/* <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder=" Search by Job Role"
                                    name="project-search"
                                    autoComplete="off"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`w-56 ${theme === "dark" ? "glass-effect text-[#EDEDEDE5]" : "bg-bgPrimary !text-textPrimary"} border border-gray-600 rounded-full pl-10 pr-4 placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white text-sm h-10 placeholder:text-sm`}
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-textPrimary"
                                    >
                                        <XIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => navigate("/org-admin/sourcing/sequences")}
                                className={`flex items-center justify-center gap-2 ${theme === "dark" ? "glass-effect" : "bg-bgPrimary"} border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light`}
                            >
                                Outreach
                            </button>
                            <button
                                onClick={() => navigate("/org-admin/sourcing")}
                                className="flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white hover:opacity-90"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                New search
                            </button>
                        </div> */}
                    </div>

                    <div className="w-full flex flex-col flex-1 min-h-0 pl-6 mt-10">
                        <div className="w-full flex-1 overflow-y-auto pb-8 min-h-[400px]">
                            {loading ? (
                                <div className="flex justify-center py-24">
                                    <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : error ? (
                                <p className="text-sm text-red-500 dark:text-red-400 text-center py-24">{error}</p>
                            ) : currentJobRoles.length === 0 ? (
                                <div className="flex h-60 items-center justify-center">
                                    <div className="flex flex-col items-center text-center">
                                        <h2 className="text-lg font-medium text-gray-400 dark:text-gray-500 mb-1">No job roles found</h2>
                                        <p className="text-sm text-gray-400 dark:text-gray-500">Run a search against a job role to see it here.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-1">
                                    {showAddCard && (
                                        <div
                                            onClick={() => navigate("/org-admin/sourcing")}
                                            className="group cursor-pointer rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-2 text-center p-5 min-h-[104px] hover:border-purple-400 transition-all"
                                        >
                                            <span className="w-8 h-8 rounded-full border border-purple-400 flex items-center justify-center text-purple-500 dark:text-purple-300 group-hover:bg-purple-50 dark:group-hover:bg-purple-500/10 transition-colors">
                                                <Plus className="w-4 h-4" />
                                            </span>
                                            <span className="text-sm text-gray-500 dark:text-gray-300 group-hover:text-purple-600 transition-colors">
                                                New search
                                            </span>
                                        </div>
                                    )}
                                    {currentJobRoles.map((j) => (
                                        <div
                                            key={j.job_id}
                                            onClick={() => navigate(`/org-admin/sourcing/projects/${j.job_id}`)}
                                            className="cursor-pointer rounded-2xl border border-gray-200 dark:border-gray-700/50 bg-transparent p-5 min-h-[104px] flex flex-col justify-center"
                                        >
                                            <h3
                                                className="text-base font-medium dark:text-[#E6E6E6] text-textPrimary line-clamp-1 break-words"
                                                title={j.job_role}
                                            >
                                                {j.job_role}
                                            </h3>
                                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                                {j.search_count ?? 0} {(j.search_count ?? 0) === 1 ? "search" : "searches"} · Last activity {relTime(j.last_activity)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* {!loading && !error && totalItems > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalItems={totalItems}
                                itemsPerPage={itemsPerPage}
                                onPageChange={(p) => setCurrentPage(p)}
                                onItemsPerPageChange={(size) => {
                                    setItemsPerPage(size);
                                    setCurrentPage(1);
                                }}
                                label="Job Roles"
                            />
                        )} */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Projects;
